import { API } from '@/utils/API';

export interface Document {
  id: string;
  filename: string;
  document_category: string | null;
  document_status: string;
  submission_id: string;
  download_url: string | null;
  view_url: string | null;
  created_at: string;
  pi_name: string | null;
  question_slug?: string;
}

export interface SubmissionDocumentsResponse {
  submission_id: string;
  documents: Document[];
}

export function submissionDocumentsQueryKey(submissionId: string) {
  return ['submission-documents', submissionId] as const;
}

export async function fetchSubmissionDocuments(
  submissionId: string
): Promise<Document[]> {
  const response = await API.authenticated.fetch(
    `/submissions/${submissionId}/documents`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch documents');
  }

  const data: SubmissionDocumentsResponse = await response.json();
  return data.documents ?? [];
}

export async function uploadSubmissionDocument({
  submissionId,
  questionSlug,
  file,
  onProgress,
}: {
  submissionId: string;
  questionSlug: string;
  file: File;
  /** Called with the integer percent (0–100) of bytes sent to the server. */
  onProgress?: (percent: number) => void;
}): Promise<Document> {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('question_slug', questionSlug);
  formData.append('submission_id', submissionId);

  const response = await API.authenticated.uploadWithProgress(
    `/submissions/${submissionId}/documents`,
    {
      method: 'POST',
      body: formData,
      onProgress: onProgress
        ? ({ loaded, total }) =>
            onProgress(total > 0 ? Math.round((loaded / total) * 100) : 0)
        : undefined,
    }
  );

  if (!response.ok) {
    const data = await response
      .json()
      .catch(() => ({ error: 'Failed to upload file' }));
    throw new Error(data.error ?? 'Failed to upload file');
  }

  return response.json();
}

export async function deleteSubmissionDocument({
  submissionId,
  documentId,
}: {
  submissionId: string;
  documentId: string;
}): Promise<void> {
  const response = await API.authenticated.fetch(
    `/submissions/${submissionId}/documents/${documentId}`,
    { method: 'DELETE' }
  );

  if (!response.ok) {
    const data = await response
      .json()
      .catch(() => ({ error: 'Failed to remove file' }));
    throw new Error(data.error ?? 'Failed to remove file');
  }
}

export async function updateDocumentCategory(
  submissionId: string,
  documentId: string,
  category: string
): Promise<void> {
  const response = await API.authenticated.fetch(
    `/submissions/${submissionId}/documents/${documentId}`,
    {
      method: 'PATCH',
      body: JSON.stringify({ document_category: category }),
    }
  );
  if (!response.ok) throw new Error('Failed to update document category');
}
